import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Check, X, FileQuestion, Loader2, Clock, CheckCircle2, XCircle } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import api from "@/lib/api";
import { RIGHT_DEFS } from "@/lib/rights";
import { formatDate } from "@/lib/format";

const STATUS = {
  pending: { icon: Clock, cls: "border-amber-200 bg-amber-50 text-amber-800" },
  fulfilled: { icon: CheckCircle2, cls: "border-emerald-200 bg-emerald-50 text-emerald-700" },
  declined: { icon: XCircle, cls: "border-red-200 bg-red-50 text-red-700" },
};

export const InvestorRightsPanel = ({ companyId }) => {
  const [rights, setRights] = useState(null);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [r, rq] = await Promise.all([
        api.get(`/portal/companies/${companyId}/my-rights`),
        api.get(`/portal/companies/${companyId}/document-requests`),
      ]);
      setRights(r.data); setRequests(rq.data);
      setCategory(r.data.effective_categories?.[0] || "");
    } finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (companyId) load(); }, [companyId]);

  const submit = async () => {
    if (!category) return toast.error("Choose a document type");
    setSending(true);
    try {
      await api.post(`/portal/companies/${companyId}/document-requests`, { category, message });
      toast.success("Request sent to the founder");
      setOpen(false); setMessage(""); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Request failed"); }
    finally { setSending(false); }
  };

  if (loading || !rights) return <p className="text-sm text-muted-foreground">Loading your rights...</p>;
  const allowed = rights.effective_categories || [];

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_360px]">
      <Card className="rounded-xl border bg-card p-5" data-testid="investor-rights-panel">
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-primary" /><h3 className="h-display text-base font-semibold">Your Information Rights</h3></div>
          <Badge className="border-primary/30 bg-primary/10 text-primary">{rights.tier}</Badge>
        </div>
        <p className="text-sm text-muted-foreground">Reporting cadence: <span className="font-medium capitalize text-foreground">{rights.reporting_cadence}</span></p>
        {rights.notes && <p className="mt-2 rounded-lg bg-secondary/60 p-3 text-sm text-foreground">{rights.notes}</p>}
        <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
          {RIGHT_DEFS.map((rd) => {
            const on = !!rights.rights?.[rd.k];
            return (
              <div key={rd.k} className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${on ? "border-emerald-200 bg-emerald-50/60 text-foreground" : "border-dashed bg-muted/40 text-muted-foreground"}`} data-testid={`right-${rd.k}`}>
                {on ? <Check className="h-4 w-4 text-emerald-600" /> : <X className="h-4 w-4 text-muted-foreground" />}
                <span className={on ? "" : "line-through decoration-muted-foreground/40"}>{rd.label}</span>
              </div>
            );
          })}
        </div>
        <div className="mt-4">
          <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Documents you may request</div>
          <div className="flex flex-wrap gap-1.5">
            {allowed.length ? allowed.map((c) => <Badge key={c} variant="secondary" className="font-normal">{c}</Badge>) : <span className="text-sm text-muted-foreground">None granted.</span>}
          </div>
        </div>
      </Card>

      <Card className="h-fit rounded-xl border bg-card p-5">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-foreground">Document requests</h3>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button size="sm" className="gap-1.5" disabled={allowed.length === 0} data-testid="request-document-button"><FileQuestion className="h-4 w-4" /> Request</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Request a document</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">You can only request documents included in your information rights.</p>
                <div>
                  <Label>Document type</Label>
                  <Select value={category} onValueChange={setCategory}>
                    <SelectTrigger className="mt-1" data-testid="request-category"><SelectValue placeholder="Choose..." /></SelectTrigger>
                    <SelectContent>{allowed.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Message (optional)</Label><Textarea value={message} onChange={(e) => setMessage(e.target.value)} className="mt-1" data-testid="request-message" /></div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={submit} disabled={sending} className="gap-2" data-testid="request-submit">{sending ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Send request</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
        <div className="mt-3 space-y-2">
          {requests.length === 0 ? <p className="text-sm text-muted-foreground">No requests yet.</p> : requests.map((r) => {
            const st = STATUS[r.status] || STATUS.pending;
            return (
              <div key={r.id} className="rounded-lg border p-3" data-testid={`my-request-${r.id}`}>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">{r.category}</span>
                  <Badge className={`gap-1 ${st.cls}`}><st.icon className="h-3 w-3" />{r.status}</Badge>
                </div>
                {r.message && <p className="mt-1 text-xs text-muted-foreground">{r.message}</p>}
                {r.resolution_note && <p className="mt-1 text-xs text-muted-foreground">Founder: {r.resolution_note}</p>}
                <p className="mt-1 text-[11px] text-muted-foreground">{formatDate(r.created_at)}</p>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
};
